import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@/contexts/FeatureFlags/useFeatureFlags';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { managementAreaSidebarItems, ManagementAreaSidebarItem } from './managementAreaSidebarData';
import ManagementAreaNavItem from './ManagementAreaNavItem';
import SidebarPinToggle from '@/components/sidebar/SidebarPinToggle';
import styles from './ManagementAreaSidebar.module.css';

interface ManagementAreaSidebarProps {
  isExpanded?: boolean;
  isPinned: boolean;
  isRail: boolean;
  onTogglePinned: () => void;
}

const ManagementAreaSidebar: React.FC<ManagementAreaSidebarProps> = ({
  isExpanded = true,
  isPinned,
  isRail,
  onTogglePinned,
}) => {
  const { t } = useTranslation();
  const { isEnabled } = useFeatureFlags();

  /*
   * פריט עם featureKey כבוי לא מוצג בכלל, וקבוצה שנשארה בלי ילדים נעלמת איתו -
   * אחרת התפריט משאיר קבוצה שנפתחת לכלום. הסתרה גם בזמן שהמפה נטענת, שזה הכיוון
   * הבטוח: עדיף פריט שמופיע רגע באיחור מפיצ'ר שהוסר ומהבהב על המסך.
   */
  const visibleItems = useMemo(() => {
    const filter = (items: ManagementAreaSidebarItem[]): ManagementAreaSidebarItem[] =>
      items
        .filter((item) => !item.featureKey || isEnabled(item.featureKey))
        .map((item) => (item.children ? { ...item, children: filter(item.children) } : item))
        .filter((item) => !item.children || item.children.length > 0);

    return filter(managementAreaSidebarItems);
  }, [isEnabled]);

  const renderNavItem = (
    item: ManagementAreaSidebarItem,
    index: number | string,
    depth = 0,
    siblings: ManagementAreaSidebarItem[] = visibleItems
  ) => {
    /*
     * NavLink מסמן פריט כפעיל על כל נתיב שמתחיל בנתיב שלו, ולכן "אישור פרויקטים"
     * (/management/projects) נדלק גם כשהמסך הפתוח הוא /management/projects/suppliers,
     * ושני הילדים נראים נבחרים יחד (HAT-1235). פריט שאחד מאחיו יושב מתחתיו בנתיב
     * מקבל התאמה מדויקת; כל השאר נשארים עם התאמת-קידומת, כדי שמסך פירוט
     * (למשל inquiries/:inquiryId) ימשיך להדליק את הפריט שלו.
     */
    const hasSiblingBelowIt = Boolean(item.to) && siblings.some((s) => s !== item && s.to?.startsWith(`${item.to}/`));
    return (
      <ManagementAreaNavItem
        sideBarItem={item}
        key={index}
        to={item.to}
        end={hasSiblingBelowIt}
        isCollapsed={!isExpanded}
        depth={depth}
        isRail={isRail}
        onRequestExpand={onTogglePinned}
      >
        {item.children?.map((child, childIndex) =>
          renderNavItem(child, `${index}-${childIndex}`, depth + 1, item.children)
        )}
      </ManagementAreaNavItem>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        <div className={styles.tag}>{t('managementArea.title')}</div>
      </div>
      <div className={styles.pinToggle}>
        <SidebarPinToggle isPinned={isPinned} onToggle={onTogglePinned} />
      </div>
      <div className={styles.content}>
        <nav className={styles.nav} aria-label="Management Navigation">
          {/* רק הפריטים גוללים. "חזרה לאתר" יושב מחוץ לאזור הגלילה, ולכן הוא
              נצמד לתחתית המסך ולא זז כשהעמוד נגלל (HAT-1374). */}
          <div className={styles.navScroll}>
            <div className={styles.navItems}>
              {visibleItems.map((item, index) => renderNavItem(item, index))}
            </div>
          </div>
          <div className={styles.bottom}>
            <Link to="/" className={`${styles.navItem} ${styles.backLink}`} title={t('managementArea.backToSite')}>
              <span className={styles.iconHolder}>
                <ArrowBackIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} className={styles.backIcon} />
              </span>
              <span className={styles.textGroup}>
                <span className={styles.label}>{t('managementArea.backToSite')}</span>
              </span>
            </Link>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default ManagementAreaSidebar;
